import { LANE_COUNT } from '../sim/constants';
import type { RoomDef } from '../sim/room';
import { Rng } from '../sim/rng';

/** Chunk size in tiles. Every chunk in every set has these dimensions. */
export const CHUNK_COLS = 24;
export const CHUNK_ROWS = 17;
/** Ground rows at the seams (the top one is the floor the player walks on). */
const SEAM_GROUND_ROWS = [14, 15, 16];
const SEAM_COLS = 2;

/** One hand-made piece of a room: an ASCII grid per lane, in the room format. */
export interface ChunkDef {
  readonly id: string;
  readonly lanes: readonly (readonly string[])[];
}

/** A world's chunks: a start pad (holds `P`), interchangeable middles, and an exit (holds `X`). */
export interface ChunkSet {
  readonly start: ChunkDef;
  readonly middle: readonly ChunkDef[];
  readonly exit: ChunkDef;
}

/** Middle chunks per room: a room is start + this many + exit (5 chunks, 120 tiles). */
export const MIDDLE_CHUNKS_PER_ROOM = 3;

/**
 * Builds one room from a seed (the Dead Cells method in miniature): the start pad, then middle
 * chunks in a seeded order with no repeats, then the exit. The same seed always builds the same
 * room, so a run can be replayed and a bug report carries its seed.
 */
export function buildRoom(set: ChunkSet, seed: string, name: string): RoomDef {
  const rng = Rng.fromSeed(seed);
  const middles = rng.shuffle([...set.middle]).slice(0, MIDDLE_CHUNKS_PER_ROOM);
  const sequence = [set.start, ...middles, set.exit];
  const lanes = Array.from({ length: LANE_COUNT }, (_, lane) =>
    Array.from({ length: CHUNK_ROWS }, (_, row) =>
      sequence.map((chunk) => chunk.lanes[lane]?.[row] ?? '').join(''),
    ),
  );
  return { id: `${seed} [${sequence.map((c) => c.id).join(' ')}]`, name, lanes };
}

/** Seed for one room of one planet in a run. */
export function roomSeed(runSeed: string, planet: string, room: number): string {
  return `run:${runSeed}/planet:${planet}/room:${room}`;
}

/**
 * Everything wrong with a chunk set: sizes, the seam rule (ground at both edges with nothing
 * above, in every lane), and where `P` and `X` may appear.
 */
export function chunkSetProblems(set: ChunkSet): string[] {
  const problems: string[] = [];
  const all = [set.start, ...set.middle, set.exit];
  for (const chunk of all) {
    const say = (msg: string) => problems.push(`${chunk.id}: ${msg}`);
    if (chunk.lanes.length !== LANE_COUNT) say(`${chunk.lanes.length} lanes`);
    chunk.lanes.forEach((grid, lane) => {
      if (grid.length !== CHUNK_ROWS) say(`lane ${lane} has ${grid.length} rows`);
      grid.forEach((row, y) => {
        if (row.length !== CHUNK_COLS) say(`lane ${lane} row ${y} is ${row.length} wide`);
      });
      const edges = [
        ...Array.from({ length: SEAM_COLS }, (_, i) => i),
        // The exit chunk ends the room, so only its left edge must meet another chunk.
        ...(chunk === set.exit
          ? []
          : Array.from({ length: SEAM_COLS }, (_, i) => CHUNK_COLS - 1 - i)),
      ];
      for (const x of edges) {
        grid.forEach((row, y) => {
          const ch = row[x];
          const wantGround = SEAM_GROUND_ROWS.includes(y);
          if (wantGround && ch !== '#')
            say(`lane ${lane} seam column ${x} row ${y} must be ground`);
          if (!wantGround && ch !== '.')
            say(`lane ${lane} seam column ${x} row ${y} must be empty`);
        });
      }
    });
    const text = chunk.lanes.flat().join('');
    const starts = text.split('P').length - 1;
    const exits = text.split('X').length - 1;
    if (chunk === set.start && (starts !== 1 || !chunk.lanes[0]?.join('').includes('P'))) {
      say('the start chunk needs exactly one P, in lane 0');
    }
    if (chunk !== set.start && starts > 0) say('only the start chunk may hold P');
    if (chunk === set.exit && exits === 0) say('the exit chunk needs an X');
    if (chunk !== set.exit && exits > 0) say('only the exit chunk may hold X');
  }
  if (set.middle.length < MIDDLE_CHUNKS_PER_ROOM) {
    problems.push(`needs at least ${MIDDLE_CHUNKS_PER_ROOM} middle chunks`);
  }
  return problems;
}
